import { apiClient, type ApiResponse, type RequestConfig } from '@shared/api/api-client';
import type {
  LogisticsCatalogItem,
  TravelPlanDay,
  TravelPlanGlobalFeeTemplate,
  TravelPlanPaymentPolicyUpdateRequest,
  TravelPlanPaymentPolicyUpdateResponse,
  TravelPlanningLoadResponse,
} from '@jema-manager/shared';

export const travelPlanningApi = {
  noticeStatus: async (travelCaseId: string) => {
    const response = await apiClient.get<
      ApiResponse<
        Array<{
          notice_type: 'planning' | 'finalized' | 'cancelled';
          app_status: string;
          sms_status: string;
          last_error: string | null;
        }>
      >
    >(`/admin/travel-planning/${encodeURIComponent(travelCaseId)}/notice-status`);
    return response.data;
  },
  retryFinalNotice: async (travelCaseId: string) => {
    const response = await apiClient.post<ApiResponse<{ appStatus: string; smsStatus: string }>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/retry-final-notice`,
      {}
    );
    return response.data;
  },
  retryCancellationNotice: async (travelCaseId: string) => {
    const response = await apiClient.post<ApiResponse<{ appStatus: string }>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/retry-cancel-notice`,
      {}
    );
    return response.data;
  },
  load: async (
    travelCaseId: string,
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<TravelPlanningLoadResponse> => {
    const response = await apiClient.get<ApiResponse<TravelPlanningLoadResponse>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}`,
      undefined,
      config
    );
    return response.data;
  },

  saveDays: async (travelCaseId: string, days: TravelPlanDay[]) => {
    const response = await apiClient.post<ApiResponse<{ travelCaseId: string }>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/days`,
      { days }
    );
    return response.data;
  },

  saveGlobalFees: async (travelCaseId: string, globalFees: TravelPlanGlobalFeeTemplate[]) => {
    const response = await apiClient.post<ApiResponse<{ travelCaseId: string }>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/global-fees`,
      { globalFees }
    );
    return response.data;
  },

  finalize: async (travelCaseId: string) => {
    const response = await apiClient.post<
      ApiResponse<{
        travelCaseId: string;
        updatedQuoteIds: string[];
        noticeStatus: { appStatus: string; smsStatus: string } | null;
      }>
    >(`/admin/travel-planning/${encodeURIComponent(travelCaseId)}/finalize`, {});
    return response.data;
  },

  cancel: async (travelCaseId: string, reason: string) => {
    const response = await apiClient.post<
      ApiResponse<{ travelCaseId: string; status: 'cancelled'; cancelled: boolean; noticeStatus: { appStatus: string } | null }>
    >(`/admin/travel-planning/${encodeURIComponent(travelCaseId)}/cancel`, { reason });
    return response.data;
  },

  notifyPlanning: async (travelCaseId: string) => {
    const response = await apiClient.post<
      ApiResponse<{ travelCaseId: string; noticeType: string; sent: boolean }>
    >(`/admin/travel-planning/${encodeURIComponent(travelCaseId)}/notify-planning`, {});
    return response.data;
  },

  listCatalog: async (query?: string, config?: Omit<RequestConfig, 'method' | 'body'>) => {
    const response = await apiClient.get<ApiResponse<LogisticsCatalogItem[]>>(
      `/admin/travel-planning/catalog`,
      query ? { query } : undefined,
      config
    );
    return response.data;
  },

  createCatalogItem: async (payload: {
    label: string;
    description?: string | null;
    category: string;
    amount: number;
    currency: string;
  }) => {
    const response = await apiClient.post<ApiResponse<LogisticsCatalogItem>>(
      `/admin/travel-planning/catalog`,
      payload
    );
    return response.data;
  },

  autoApplyLogistics: async (travelCaseId: string) => {
    const response = await apiClient.post<
      ApiResponse<{ travelCaseId: string; days: TravelPlanDay[] }>
    >(`/admin/travel-planning/${encodeURIComponent(travelCaseId)}/auto-apply`, {});
    return response.data;
  },

  listPlatformDailyFees: async (
    config?: Omit<RequestConfig, 'method' | 'body'>
  ): Promise<LogisticsCatalogItem[]> => {
    const response = await apiClient.get<ApiResponse<LogisticsCatalogItem[]>>(
      `/admin/travel-planning/platform-daily-fees`,
      undefined,
      config
    );
    return response.data;
  },

  updatePaymentPolicy: async (
    travelCaseId: string,
    payload: TravelPlanPaymentPolicyUpdateRequest
  ): Promise<TravelPlanPaymentPolicyUpdateResponse> => {
    const response = await apiClient.post<ApiResponse<TravelPlanPaymentPolicyUpdateResponse>>(
      `/admin/travel-planning/${encodeURIComponent(travelCaseId)}/payment-policy`,
      payload
    );
    return response.data;
  },
};
